import { Trash2 } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { JOURNEY_ACTION_TYPES, type JourneyAction, type JourneyActionType } from "@ckgo2/shared";
import { ACTION_TYPE_LABELS } from "../labels";

export interface BuilderOptions {
  users: Array<{ id: string; name: string }>;
  segments: Array<{ id: string; name: string }>;
  templates: Array<{ slug: string; name: string }>;
}

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";
const inp = sel + " w-full";

// Literal "{{vars}}" snippets shown in UI hints. Passed as interpolation values so
// i18next renders them verbatim instead of trying to interpolate them
// (interpolation.skipOnVariables keeps the inserted value untouched).
const VARS = "{{vars}}";
const LAST_TRIP_DATE = "{{lastTripDate}}";

// All v1 action types are offered. SMS/WhatsApp are a stub channel (sends record skipped:
// channel_not_configured until the provider lands in řez 3) — the editor flags that inline.
const BUILDER_ACTION_TYPES: JourneyActionType[] = [...JOURNEY_ACTION_TYPES];

/** A well-formed default action for a freshly-picked type. */
function defaultAction(type: JourneyActionType): JourneyAction {
  switch (type) {
    case "send_email":
      return { type, recipient: "customer" };
    case "send_sms":
      return { type, recipient: "customer" };
    case "send_whatsapp":
      return { type, recipient: "customer" };
    case "create_task":
      return { type, title: "" };
    case "assign":
      return { type, mode: "round_robin" };
    case "notify_user":
      return { type, target: "subject_assignee", message: "" };
    case "add_to_segment":
      return { type, segmentId: "" };
    case "remove_from_segment":
      return { type, segmentId: "" };
    case "generate_document":
      return { type, docType: "contract" };
    case "webhook":
      return { type, url: "" };
    case "agent_action":
      return { type, instruction: "", allowMoney: false };
  }
}

export default function ActionEditor({
  value,
  onChange,
  onRemove,
  options,
}: {
  value: JourneyAction;
  onChange: (a: JourneyAction) => void;
  onRemove: () => void;
  options: BuilderOptions;
}) {
  const { t } = useTranslation();
  // Narrow per branch with a loose patcher (the union is validated server-side on save).
  const patch = (p: Record<string, unknown>) => onChange({ ...value, ...p } as JourneyAction);

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
      <div className="flex items-center justify-between">
        <select
          className={sel}
          value={value.type}
          onChange={(e) => onChange(defaultAction(e.target.value as JourneyActionType))}
        >
          {BUILDER_ACTION_TYPES.map((actionType) => (
            <option key={actionType} value={actionType}>
              {ACTION_TYPE_LABELS[actionType]}
            </option>
          ))}
        </select>
        <button
          title={t("common.actions.remove")}
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-gray-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {value.type === "send_email" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <select
              className={sel}
              value={value.recipient}
              onChange={(e) => patch({ recipient: e.target.value })}
            >
              <option value="customer">{t("marketing.journeyAction.toCustomer")}</option>
              <option value="assignee">{t("marketing.journeyAction.toAssignee")}</option>
              <option value="admin">{t("marketing.journeyAction.toAdmin")}</option>
              <option value="email_field">{t("marketing.journeyAction.toField")}</option>
            </select>
            {value.recipient === "email_field" && (
              <input
                className={sel}
                placeholder={t("marketing.journeyAction.varsKeyPlaceholder")}
                value={value.emailField ?? ""}
                onChange={(e) => patch({ emailField: e.target.value })}
              />
            )}
            <select
              className={sel}
              value={value.templateSlug ?? ""}
              onChange={(e) => patch({ templateSlug: e.target.value || undefined })}
            >
              <option value="">{t("marketing.journeyAction.inlineSubjectBody")}</option>
              {options.templates.map((tpl) => (
                <option key={tpl.slug} value={tpl.slug}>
                  {t("marketing.journeyAction.templateOption", { name: tpl.name })}
                </option>
              ))}
            </select>
          </div>
          {!value.templateSlug && (
            <>
              <input
                className={inp}
                placeholder={t("marketing.journeyAction.subjectPlaceholder", { vars: VARS })}
                value={value.subject ?? ""}
                onChange={(e) => patch({ subject: e.target.value })}
              />
              <textarea
                className={inp}
                rows={3}
                placeholder={t("marketing.journeyAction.bodyPlaceholder", { vars: VARS })}
                value={value.body ?? ""}
                onChange={(e) => patch({ body: e.target.value })}
              />
            </>
          )}
        </div>
      )}

      {(value.type === "send_sms" || value.type === "send_whatsapp") && (
        <div className="space-y-2">
          <select
            className={sel}
            value={value.recipient}
            onChange={(e) => patch({ recipient: e.target.value })}
          >
            <option value="customer">{t("marketing.journeyAction.toCustomer")}</option>
            <option value="assignee">{t("marketing.journeyAction.toAssignee")}</option>
            <option value="phone_field">{t("marketing.journeyAction.toField")}</option>
          </select>
          <textarea
            className={inp}
            rows={2}
            placeholder={t("marketing.journeyAction.messagePlaceholder", { vars: VARS })}
            value={value.body ?? ""}
            onChange={(e) => patch({ body: e.target.value })}
          />
          <p className="text-xs text-amber-600">{t("marketing.journeyAction.smsChannelHint")}</p>
        </div>
      )}

      {value.type === "create_task" && (
        <div className="space-y-2">
          <input
            className={inp}
            placeholder={t("marketing.journeyAction.taskTitlePlaceholder", { vars: VARS })}
            value={value.title}
            onChange={(e) => patch({ title: e.target.value })}
          />
          <div className="flex flex-wrap gap-2">
            <input
              type="number"
              className={sel + " w-28"}
              placeholder={t("marketing.journeyAction.dueInDays")}
              value={value.dueInDays ?? ""}
              onChange={(e) =>
                patch({ dueInDays: e.target.value === "" ? undefined : Number(e.target.value) })
              }
            />
            <select
              className={sel}
              value={value.kind ?? "manual"}
              onChange={(e) => patch({ kind: e.target.value })}
            >
              <option value="manual">{t("marketing.journeyAction.kindTask")}</option>
              <option value="callback">{t("marketing.journeyAction.kindCallback")}</option>
              <option value="supplier_followup">
                {t("marketing.journeyAction.kindSupplierFollowup")}
              </option>
            </select>
            <select
              className={sel}
              value={value.assignTo ?? ""}
              onChange={(e) => patch({ assignTo: e.target.value || undefined })}
            >
              <option value="">{t("marketing.journeyAction.unassigned")}</option>
              <option value="subject_assignee">{t("marketing.journeyAction.subjectOwner")}</option>
              <option value="user">{t("marketing.journeyAction.specificUserEllipsis")}</option>
            </select>
            {value.assignTo === "user" && (
              <select
                className={sel}
                value={value.userId ?? ""}
                onChange={(e) => patch({ userId: e.target.value })}
              >
                <option value="">—</option>
                {options.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {value.type === "assign" && (
        <div className="flex flex-wrap gap-2">
          <select
            className={sel}
            value={value.mode}
            onChange={(e) => patch({ mode: e.target.value })}
          >
            <option value="round_robin">{t("marketing.journeyAction.roundRobin")}</option>
            <option value="user">{t("marketing.journeyAction.specificUser")}</option>
          </select>
          {value.mode === "user" && (
            <select
              className={sel}
              value={value.userId ?? ""}
              onChange={(e) => patch({ userId: e.target.value })}
            >
              <option value="">—</option>
              {options.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {value.type === "notify_user" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <select
              className={sel}
              value={value.target}
              onChange={(e) => patch({ target: e.target.value })}
            >
              <option value="subject_assignee">{t("marketing.journeyAction.subjectOwner")}</option>
              <option value="user">{t("marketing.journeyAction.specificUser")}</option>
            </select>
            {value.target === "user" && (
              <select
                className={sel}
                value={value.userId ?? ""}
                onChange={(e) => patch({ userId: e.target.value })}
              >
                <option value="">—</option>
                {options.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <input
            className={inp}
            placeholder={t("marketing.journeyAction.notificationPlaceholder", { vars: VARS })}
            value={value.message}
            onChange={(e) => patch({ message: e.target.value })}
          />
        </div>
      )}

      {(value.type === "add_to_segment" || value.type === "remove_from_segment") && (
        <select
          className={sel}
          value={value.segmentId}
          onChange={(e) => patch({ segmentId: e.target.value })}
        >
          <option value="">{t("marketing.journeyAction.pickSegment")}</option>
          {options.segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      )}

      {value.type === "generate_document" && (
        <select
          className={sel}
          value={value.docType}
          onChange={(e) => patch({ docType: e.target.value })}
        >
          <option value="contract">{t("marketing.journeyAction.docContract")}</option>
          <option value="itinerary">{t("marketing.journeyAction.docItinerary")}</option>
          <option value="voucher">{t("marketing.journeyAction.docVoucher")}</option>
          <option value="package">{t("marketing.journeyAction.docPackage")}</option>
        </select>
      )}

      {value.type === "webhook" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <select
              className={sel}
              value={value.method ?? "POST"}
              onChange={(e) => patch({ method: e.target.value })}
            >
              <option value="POST">POST</option>
              <option value="GET">GET</option>
            </select>
            <input
              className={inp}
              placeholder="https://…"
              value={value.url}
              onChange={(e) => patch({ url: e.target.value })}
            />
          </div>
          <textarea
            className={inp}
            rows={2}
            placeholder={t("marketing.journeyAction.webhookBodyPlaceholder", { vars: VARS })}
            value={value.bodyTemplate ?? ""}
            onChange={(e) => patch({ bodyTemplate: e.target.value || undefined })}
          />
        </div>
      )}

      {value.type === "agent_action" && (
        <div className="space-y-2">
          <textarea
            className={inp}
            rows={3}
            placeholder={t("marketing.journeyAction.agentInstructionPlaceholder", {
              vars: VARS,
              lastTripDate: LAST_TRIP_DATE,
            })}
            value={value.instruction}
            onChange={(e) => patch({ instruction: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={value.allowMoney ?? false}
              onChange={(e) => patch({ allowMoney: e.target.checked })}
            />
            {t("marketing.journeyAction.allowMoney")}
          </label>
          <p className="text-xs text-gray-500">
            <Trans i18nKey="marketing.journeyAction.agentHint" />
          </p>
        </div>
      )}
    </div>
  );
}
